import type { UpdateContactInput } from 'app/generated/gql/graphql'
import { observer } from 'mobx-react-lite'
import { useSearchParams } from 'react-router-dom'
import { ColumnDef, SortingState } from '@tanstack/react-table'
import moment from 'moment'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { ItemsTitle } from './styled/ItemsTitle'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import SimpleTable from 'app/components/table/SimpleTable'
import { addDefaultFilterProperties, FilterableColumn, FilterValueType, PredefinedFilter } from 'app/filter.utils'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListTraversal } from 'app/hooks/useListTraversal'
import type { AdCampaign } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore } from 'app/stores/StoreContext'
import { defaultColumns } from 'app/table-columns/marketing-details'
import { hasRole, useUser } from 'app/utils'
import { bg } from 'lib/background'
import { comActivityTypes } from 'lib/comActivityTypes'
import { isSMCOrg } from 'lib/isSMCOrg'
import { tagFilterOptionsFetcher } from 'app/lib/fetchers/tagFilterOptionsFetcher'
import { debug, logWarn } from 'lib/logging'

type Props = {
  title: string
  subtitle?: string
  defaultFilters?: PredefinedFilter
  items?: ContactJson[]
  total: number
  enableDeepLinking: boolean
  isManuallySorted?: boolean
  activeSubpage?: string
  customColumns?: (
    contactHandler: (contactId: string, patientIds?: string[]) => void,
    updateContact: (contact: UpdateContactInput & { id?: string }) => void,
    closeDrawerHandler: () => void,
    isSMC: boolean,
    isSuper: boolean,
    staffDashboardV2?: boolean,
  ) => ColumnDef<ContactJson, any>[]
  closeHandler?: () => void
  filtersHandler?: (filters: string) => void
  searchValueHandler?: (searchValue: string) => void
  scrollHandler?: (event: React.UIEvent<HTMLDivElement>) => void
  sortHandler: (sortString: string) => void
  /** Hide the table filter toolbar row. */
  hideFilters?: boolean
  /** Override the default filterable columns. */
  customFilterableColumns?: FilterableColumn[]
  /** Hide the column ordering button. */
  hideColumnOrdering?: boolean
}

const filterableColumns = (
  addMarketingFilters: boolean,
  isSuper: boolean,
  defaultFilters?: PredefinedFilter,
): FilterableColumn[] =>
  [
    {
      attribute: `name`,
      displayName: `Name`,
      type: FilterValueType.string,
    },
    {
      attribute: `locationId`,
      displayName: `Location`,
      optionsSource: `/api/locations`,
      type: FilterValueType.id,
      uniqOperator: `in`,
    } as FilterableColumn,
    {
      attribute: `tags`,
      displayName: `Tags`,
      fetcher: (params) => tagFilterOptionsFetcher(`Contact`, params),
      type: FilterValueType.stringArray,
    },
    {
      attribute: `channel.keyword`,
      displayName: `Channel`,
      type: FilterValueType.asyncSelect,
      nonEmpty: true,
      optionsSource: `/api/channels`,
    },
    {
      attribute: `leadStatus.keyword`,
      displayName: `Lead Status`,
      type: FilterValueType.multiple,
      options: [`Needs Action`, `In Progress`, `Scheduled`, `Closed Won`, `Closed Lost`].map((option) => ({
        id: option,
        name: option,
        value: option,
      })),
    },
    ...(isSuper
      ? [
          {
            attribute: `neededAction`,
            displayName: `Needed Action`,
            type: FilterValueType.boolean,
            options: [
              {
                id: `yes`,
                name: `Yes`,
                value: `true`,
              },
              {
                id: `no`,
                name: `No`,
                value: `(NOT _exists_:neededAction)`,
              },
            ],
          },
        ]
      : []),
    {
      attribute: `lifecycleStage.keyword`,
      displayName: `Lifecycle Stage`,
      type: FilterValueType.multiple,
      options: [`Lead`, `Qualified Lead`, `New Patient Scheduled`, `Patient`].map((option) => ({
        id: option,
        name: option,
        value: option,
      })),
    },
    {
      attribute: `firstActivityType`,
      displayName: `First Activity Type`,
      type: FilterValueType.multiple,
      options: comActivityTypes.map((type) => ({ id: type, value: type, name: type })),
    },
    {
      attribute: `firstContactReason`,
      displayName: `First Contact Reason`,
      type: FilterValueType.string,
    },
    {
      attribute: `becameLeadDate`,
      displayName: `Became a Lead Date`,
      type: FilterValueType.rangeDate,
      defaultValue: [moment().startOf(`week`).format(`YYYY-MM-DD`), moment().endOf(`week`).format(`YYYY-MM-DD`)],
    },
    {
      attribute: `firstAppointmentMadeDate`,
      displayName: `First Appointment Made Date`,
      type: FilterValueType.rangeDate,
      defaultValue: [moment().startOf(`week`).format(`YYYY-MM-DD`), moment().endOf(`week`).format(`YYYY-MM-DD`)],
    },
    {
      attribute: `becamePatientDate`,
      displayName: `Became a Patient Date`,
      type: FilterValueType.rangeDate,
      defaultValue: [moment().startOf(`week`).format(`YYYY-MM-DD`), moment().endOf(`week`).format(`YYYY-MM-DD`)],
    },
    {
      attribute: `activityTypes`,
      displayName: `Has Activity`,
      type: FilterValueType.multiple,
      options: comActivityTypes?.map((option) => ({
        id: option,
        name: option,
        value: option,
      })),
    },
    {
      attribute: `productionLtv`,
      displayName: `Net Production`,
      type: FilterValueType.number,
    },
    {
      attribute: `isQualified`,
      displayName: `Qualified`,
      type: FilterValueType.boolean,
      options: [
        { id: `qualified`, value: `true`, name: `Yes` },
        { id: `not_qualified`, value: `false`, name: `No` },
      ],
    },
    {
      attribute: `patientIds`,
      displayName: `Patient Linked`,
      type: FilterValueType.attributeExists,
      options: [
        { id: `Linked`, value: `true`, name: `Linked` },
        { id: `Not Linked`, value: `false`, name: `Not Linked` },
      ],
    },
    {
      attribute: `patientStatus`,
      displayName: `Patient Status`,
      type: FilterValueType.multiple,
      options: [
        { id: `Active`, value: `Active`, name: `Active` },
        { id: `Inactive`, value: `Inactive`, name: `Inactive` },
        { id: `NonPatient`, value: `NonPatient`, name: `Non-Patient` },
        { id: `blank`, value: `(NOT _exists_:patientStatus)`, name: `Blank` },
      ],
    },
    ...(addMarketingFilters
      ? [
          {
            attribute: `trackableCampaignId`,
            displayName: `Ad Campaign`,
            optionsSource: `/api/campaigns`,
            type: FilterValueType.id,
            optionsMapper: (res) => {
              return res.hits
                ?.sort((a, b) => {
                  if (a.type && b.type) return a.type?.localeCompare(b?.type)
                  return -1
                })
                ?.map((entry) => ({
                  id: entry.id,
                  value: entry.id,
                  name: entry.name,
                  meta: {
                    icon: (function (type: AdCampaign[`type`]) {
                      let iconPath = ``
                      switch (type) {
                        case `google`:
                          iconPath = `/public/google_ads.png`
                          break
                        case `facebook`:
                          iconPath = `/public/facebook_ads.png`
                          break
                        default:
                          iconPath = ``
                          break
                      }
                      return iconPath
                    })(entry.type?.toLowerCase()),
                  },
                }))
            },
          },
          {
            attribute: `utmSource.keyword`,
            displayName: `Source`,
            type: FilterValueType.string,
          },
          {
            attribute: `utmMedium.keyword`,
            displayName: `Medium`,
            type: FilterValueType.string,
          },
          {
            attribute: `utmCampaign.keyword`,
            displayName: `Campaign`,
            type: FilterValueType.string,
          },
          {
            attribute: `reportedAttribution.keyword`,
            displayName: `Reported Attribution`,
            type: FilterValueType.string,
          },
          {
            attribute: `platform`,
            displayName: `Ad Platform`,
            type: FilterValueType.boolean,
            options: [
              {
                id: `facebook`,
                name: `Facebook Ads`,
                value: `Facebook Ads`,
                meta: { icon: `/public/meta_icon.png` },
              },
              {
                id: `google`,
                name: `Google Ads`,
                value: `Google Ads`,
                meta: { icon: `/public/google_ads.png` },
              },
              {
                id: `local-service-ads`,
                name: `Local Service Ads`,
                value: `Local Service Ads`,
                meta: { icon: `/public/google_ads.png` },
              },
            ],
          },
        ]
      : []),
  ].map((f) => ({ ...f, ...addDefaultFilterProperties(f.attribute, defaultFilters) }))

/**
 * This component is used to display a table of contacts in a modal.
 */
// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const ContactsTable = observer(function ContactsTable({
  title,
  subtitle,
  defaultFilters,
  items,
  total = 0,
  enableDeepLinking,
  isManuallySorted,
  activeSubpage,
  customColumns,
  closeHandler,
  filtersHandler,
  searchValueHandler,
  scrollHandler,
  sortHandler,
  hideFilters,
  customFilterableColumns,
  hideColumnOrdering,
}: Props): ReactElement {
  const me = useUser()
  if (!me) return <></>
  const isSMC = isSMCOrg(me.orgId)
  const isSuper = me.type === `super`
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const staffDashboardV2 = useFeatureToggle(`staff-dashboard-v2`)
  const checkPermission = useCheckPermission()
  const contactStore = useContactStore()
  const [loadingContact, setLoadingContact] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [focusedContact, setFocusedContact] = useState<ContactJson>()
  const [tableItems, setTableItems] = useState<ContactJson[] | undefined>(items)
  const [openDrawer, setOpenDrawer] = useState<boolean>(false)

  const contactTraversal = useListTraversal<ContactJson>(tableItems ?? [], focusedContact, (item) => {
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    if (item.id) contactHandler(item.id)
  })

  const drawerSearchParam = useMemo(() => searchParams.get(`drawer`), [searchParams])

  // Legacy role-based logic (staff-v2 OFF)
  const legacyAddMarketingFilters =
    isSuper || (isSMC && hasRole(me, `orgAdmin`)) || (!isSMC && !hasRole(me, `orgAssistant`))
  // Permission-based logic (staff-v2 ON)
  const permissionAddMarketingFilters = isSuper || checkPermission(`org:marketing:advanced-filters`)
  const addMarketingFilters = staffV2Enabled ? permissionAddMarketingFilters : legacyAddMarketingFilters

  const defaultFiltersKey = JSON.stringify(defaultFilters)
  const memoizedFilterableColumns = useMemo(
    () => filterableColumns(addMarketingFilters, isSuper, defaultFilters),
    [addMarketingFilters, isSuper, defaultFiltersKey],
  )

  const closeDrawerOnInlineEdit = () => {
    if (openDrawer) {
      closeDrawerHandler()
    }
  }

  const closeDrawerHandler = () => {
    setFocusedContact(undefined)
    setOpenDrawer(false)
    setSearchParams((prev) => {
      prev.delete(`drawer`)
      return prev
    })
  }

  const contactHandler = (contactId: string) => {
    if (focusedContact?.id === contactId) return
    setSearchParams((prev) => {
      prev.set(`drawer`, contactId)
      return prev
    })
    bg(async () => {
      setFocusedContact(undefined)
      setOpenDrawer(true)
      setLoadingContact(true)
      try {
        const result = await contactStore.fetchOne(contactId)
        const data = (result.data as any)?.contact as ContactJson | undefined
        if (!data || result.error) {
          notifyError((result.error as any)?.message ?? `Contact not found`)
          setFocusedContact(undefined)
          setOpenDrawer(false)
        } else {
          setFocusedContact(data)
          setOpenDrawer(true)
        }
      } finally {
        setLoadingContact(false)
      }
    })
  }

  const loadContact = (contact?: ContactJson) => {
    if (!contact?.id) {
      return
    }

    bg(async () => {
      setLoadingContact(true)
      try {
        const result = await contactStore.fetchOne(contact.id!)
        const response = (result.data as any)?.contact as ContactJson | undefined
        if (response?.id) {
          setFocusedContact(response)
        } else {
          notifyError(`Contact not found`)
          const url = new URL(document.location.href)
          url.searchParams.delete(`drawer`)
          history.replaceState({}, ``, url.toString())
          setFocusedContact(undefined)
          setOpenDrawer(false)
        }

        if (items && contact) {
          setTableItems(
            items?.map((hit) => {
              if (hit.id === contact.id) {
                return contact as any
              }
              return hit!
            }),
          )
        }
      } finally {
        setLoadingContact(false)
      }
    })
  }

  const updateContact = async (contact: UpdateContactInput & { id?: string }) => {
    const { id, ...input } = contact
    if (!id) return
    setLoadingContact(true)
    try {
      const result = await contactStore.update(id, input)
      if (result.error) {
        logWarn(`Error updating contact`, result.error.message)
        notifyError(`Error updating contact`)
        setTableItems((prev) => {
          let hits = prev?.map((hit) => {
            if (hit.id === id) {
              return { ...hit }
            }
            return hit
          }) as ContactJson[]
          return hits
        })
        return
      }
      const response = (result.data as any)?.contact as ContactJson | undefined
      if (response) {
        notifySuccess(`Contact updated`)
        setTableItems((prev) => {
          let hits = prev?.map((hit) => {
            if (hit.id === response.id) {
              return { ...hit, ...{ ...response, channel: response.channel ?? undefined } }
            }
            return hit
          }) as ContactJson[]
          return hits
        })
      }
    } finally {
      setLoadingContact(false)
    }
  }

  const onSortChange = (sort: SortingState) => {
    debug(`sort`, `sort changed`, sort)
    let sortString = sort
      ?.map((s) => {
        let col = columns.find((c) => c.id === s.id)
        let sortKey = col?.meta?.sortKey || s.id
        return `${sortKey}:${s.desc ? `desc` : `asc`}`
      })
      .join(`,`)
    sortHandler(sortString)
  }

  useEffect(() => setTableItems(items), [items])

  useEffect(() => {
    if (drawerSearchParam && drawerSearchParam !== `` && !openDrawer) {
      contactHandler(drawerSearchParam)
    }
  }, [drawerSearchParam])

  const columns = customColumns
    ? customColumns(contactHandler, updateContact, closeDrawerHandler, isSMC, isSuper, staffDashboardV2)
    : defaultColumns(
        contactHandler,
        updateContact,
        closeDrawerOnInlineEdit,
        checkPermission,
        addMarketingFilters,
        isSMC,
        isSuper,
        staffV2Enabled,
        staffDashboardV2,
      )

  // DO NOT REMOVE x-component="ContactModalTable" FROM THE DIV BELOW
  return (
    <div x-component="ContactModalTable" className="flex h-dvh max-h-dvh flex-col overflow-hidden px-5">
      <div className="flex h-full w-full">
        <div className="flex h-full w-full flex-col overflow-hidden">
          <ItemsTitle
            title={title}
            subtitle={subtitle}
            items={total}
            onClose={() => {
              closeHandler?.()
              closeModal()
            }}
          />
          <SimpleTable
            data={tableItems}
            columns={columns}
            initialSort={[{ id: `name`, desc: false }]}
            filterableColumns={hideFilters ? undefined : (customFilterableColumns ?? memoizedFilterableColumns)}
            meta={{ getRowStyle: (row) => (row?.id === focusedContact?.id ? `table-selected-row` : ``) }}
            enableDeepLinking={enableDeepLinking}
            isManuallySorted={isManuallySorted}
            activeSubpage={activeSubpage}
            onFiltersChange={hideFilters ? undefined : filtersHandler}
            onSearchValueChange={searchValueHandler}
            onScroll={scrollHandler}
            onSortChange={onSortChange}
            showColumnOrdering={!hideColumnOrdering}
            hideFooter
          />
        </div>
        <ContactDrawer
          openDrawer={openDrawer}
          contact={focusedContact}
          onClose={closeDrawerHandler}
          onChange={loadContact}
          nextContact={contactTraversal.next}
          previousContact={contactTraversal.prev}
          isNavigating={contactTraversal.isNavigating}
        />
      </div>
    </div>
  )
})
